import MDEditor from '@uiw/react-md-editor';
import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormGroup, FormLabel, InlineNotification, Select, SelectItem, Stack } from '@carbon/react';
import { FormikDatePicker } from 'src/components/Form/FormikDatePicker';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import ImageField from 'src/components/Storefront/ImageField';
import { StorefrontPostEntity } from 'src/services/apis/types';

/**
 * `StorefrontPostStatusEnum.DRAFT` — see the full note on `STOREFRONT_POST_STATUS` in
 * `src/features/storefrontNews.tsx`. Repeated rather than imported from there: that file
 * imports THIS component, so importing the constant back would be a cycle whose module
 * evaluation order leaves the object undefined at this line.
 */
const DRAFT = 1;
const PUBLISHED = 2;

interface NewsFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: StorefrontPostEntity | null | undefined;
}

const Schema = z.object({
  title: z.string().trim().min(1).max(200),
  slug: z.string().trim().min(1).max(120),
  excerpt: z.string().max(500).optional(),
  bodyMarkdown: z.string().max(40000).optional(),
});

/**
 * The empty value of every field, in one place.
 *
 * `resetForm` must restore ALL of them: the crud layer calls it when a modal closes, and a
 * field it forgets keeps the previous post's value in the next create — including `id`, which
 * would silently turn a create into an update of the last edited row.
 */
const EMPTY_VALUES = {
  id: undefined,
  title: '',
  slug: '',
  excerpt: '',
  bodyMarkdown: '',
  coverFileId: null,
  locale: 'vi',
  status: String(DRAFT),
  publishedAt: '',
};

const NewsForm = (props: NewsFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();

  useImperativeHandle(ref, () => ({
    submitForm: formikRef.current?.submitForm,
    setFieldValue: formikRef.current?.setFieldValue,
    resetForm: () => formikRef.current?.resetForm({ values: { ...EMPTY_VALUES } }),
  }));

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={toFormikValidationSchema(Schema)}
        initialValues={{
          id: props.initialValues?.id,
          title: props.initialValues?.title ?? '',
          slug: props.initialValues?.slug ?? '',
          excerpt: props.initialValues?.excerpt ?? '',
          bodyMarkdown: props.initialValues?.bodyMarkdown ?? '',
          coverFileId: props.initialValues?.coverFileId ?? null,
          locale: props.initialValues?.locale ?? 'vi',
          // The API returns a number (1/2) while a Carbon Select only deals in strings.
          status: String(props.initialValues?.status ?? DRAFT),
          publishedAt: props.initialValues?.publishedAt ?? '',
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              <FormikTextInput name={'title'} label={t('common.title')} />
              <FormikTextInput name={'slug'} label={t('common.slug')} helperText={t('storefront.news.slugHint')} />
              <FormikTextInput name={'excerpt'} label={t('storefront.news.excerpt')} />
              {/* The editor renders its own toolbar, and a dark wrapper breaks the Carbon theme
               * around it — the same wrapper MenuItemForm and the Content screen use. */}
              <div data-color-mode="light">
                <FormLabel>{t('storefront.news.body')}</FormLabel>
                <MDEditor
                  preview="edit"
                  height={320}
                  value={values.bodyMarkdown}
                  onChange={(value) => setFieldValue('bodyMarkdown', value ?? '')}
                />
                <p className="cds--form__helper-text">{t('storefront.news.bodyHint')}</p>
              </div>
              <ImageField
                title={t('storefront.news.cover')}
                description={t('storefront.news.coverHint')}
                aspectRatio={'16x9'}
                cropperAspectRatio={16 / 9}
                value={values.coverFileId ?? null}
                onChange={(fileId) => setFieldValue('coverFileId', fileId)}
              />
              <Select
                id="post-locale"
                labelText={t('storefront.news.locale')}
                value={values.locale}
                onChange={(event) => setFieldValue('locale', event.target.value)}
              >
                <SelectItem value="vi" text="Tiếng Việt" />
                <SelectItem value="en" text="English" />
              </Select>
              <Select
                id="post-status"
                labelText={t('storefront.news.status')}
                value={values.status}
                onChange={(event) => setFieldValue('status', event.target.value)}
              >
                <SelectItem value={String(DRAFT)} text={t('storefront.news.statusDraft')} />
                <SelectItem value={String(PUBLISHED)} text={t('storefront.news.statusPublished')} />
              </Select>
              <FormikDatePicker
                label={t('storefront.news.publishedAt')}
                value={values.publishedAt}
                onChange={(date) => setFieldValue('publishedAt', date)}
              />
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(NewsForm);
